/**
 * Top-level runtime dispatcher.
 *
 * ASP.NET/DNN selects a runtime mode (management, public form, report, ...).
 * React then mounts only the feature required by that host page. Error and
 * feedback providers wrap every mode consistently.
 */
import type { ReactNode } from 'react';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { FeedbackProvider } from '../components/Feedback';
import { CustomReportApp } from '../features/reports/CustomReportApp';
import { ManagementApp } from '../features/management/ManagementApp';
import { MySubmissionsApp } from '../features/my-submissions/MySubmissionsApp';
import { PublicFormApp } from '../features/renderer/PublicFormApp';
import { SubmissionDetailApp } from '../features/renderer/SubmissionDetailApp';
import type { DynamicFormsRuntimeConfig } from './runtime-config';
import { UiLocaleProvider, useUiLocale } from './ui-locale';


function LocalizedRuntimeRoot({ mode, children }: { mode: DynamicFormsRuntimeConfig['mode']; children: ReactNode }) {
  const { locale, direction, culture } = useUiLocale();
  return <div
    className={`df-runtime-localized-root df-runtime-localized-root--${mode}`}
    dir={direction}
    lang={locale}
    data-ui-locale={locale}
    data-ui-culture={culture}
  >{children}</div>;
}

export function App({ config }: { config: DynamicFormsRuntimeConfig }) {
  let content: React.ReactNode;
  switch (config.mode) {
    case 'form-view':
      content = <PublicFormApp formId={config.formId ?? 0} moduleId={config.moduleId ?? 0} />;
      break;
    case 'edit-submission':
      content = <PublicFormApp formId={config.formId ?? 0} submittedFormId={config.submittedFormId ?? 0} moduleId={config.moduleId ?? 0} edit />;
      break;
    case 'report-view':
      content = <SubmissionDetailApp formId={config.formId ?? 0} submittedFormId={config.submittedFormId ?? 0} />;
      break;
    case 'custom-report-view':
      content = <CustomReportApp reportId={config.reportId ?? 0} />;
      break;
    case 'my-submissions':
      content = <MySubmissionsApp pageSize={config.pageSize ?? 10} showStatus={config.showStatus !== false} showPrint={config.showPrint !== false} />;
      break;
    default:
      content = <ManagementApp editFormTabId={config.editFormTabId} bootstrapVersion={config.bootstrapVersion ?? '3'} />;
  }
  return <UiLocaleProvider culture={config.culture} persistKey={config.mode === 'management' ? 'df-admin-locale' : undefined}>
    <LocalizedRuntimeRoot mode={config.mode}>
      <ErrorBoundary><FeedbackProvider>{content}</FeedbackProvider></ErrorBoundary>
    </LocalizedRuntimeRoot>
  </UiLocaleProvider>;
}
